
import React, { useState, useEffect } from 'react';
import { ViewState, Language } from '../types';
import { translations } from '../utils/translations';

interface NavigationProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const Navigation: React.FC<NavigationProps> = ({ currentView, setView, language, setLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const t = translations[language].nav;

  const navItems = [
    { id: 'home', label: t.home, view: ViewState.HOME },
    { id: 'services', label: t.services, view: ViewState.HOME },
    { id: 'directors', label: t.directors, view: ViewState.HOME },
    { id: 'contact', label: t.contact, view: ViewState.HOME },
  ];

  const languages: Language[] = ['ES', 'EN', 'PT', 'FR', 'DE'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    navItems.forEach((item) => {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [currentView]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const handleNavClick = (item: { id: string, view: ViewState }) => {
    setActiveSection(item.id);
    if (item.view !== ViewState.HOME) {
      setView(item.view);
    } else {
      if (currentView !== ViewState.HOME) {
        setView(ViewState.HOME);
        setTimeout(() => {
          const element = document.getElementById(item.id);
          if (element) {
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
          }
        }, 150);
      } else {
        const element = document.getElementById(item.id);
        if (element) {
          const offset = 80;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
        }
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-[100] bg-black/95 backdrop-blur-md border-b border-zinc-900 h-20 flex items-center">
        <div className="w-full max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
          
          <div 
            className="flex-shrink-0 cursor-pointer z-[110] group relative h-10 w-44" 
            onClick={() => handleNavClick({ id: 'home', view: ViewState.HOME })}
          >
             <div className="relative w-full h-full animate-logo-idle transition-transform duration-300">
               <img 
                  src="https://www.irreverentia.eu/logo.png" 
                  alt="IRREVERENTIA" 
                  className="h-full w-auto object-contain absolute top-0 left-0 transition-all duration-500 opacity-100 group-hover:opacity-0"
               />
               <img 
                  src="https://www.irreverentia.eu/logo-green.png" 
                  alt="IRREVERENTIA" 
                  className="h-full w-auto object-contain absolute top-0 left-0 opacity-0 group-hover:opacity-100 drop-shadow-[0_0_8px_rgba(204,255,0,0.5)]"
               />
             </div>
          </div>

          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => {
              const isSectionActive = (currentView === ViewState.HOME || currentView === ViewState.DEMO_REEL) 
                ? activeSection === item.id 
                : item.view === currentView;

              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className="relative group py-2"
                >
                  <span className={`font-display font-bold text-sm tracking-wider transition-colors duration-300 ${isSectionActive ? 'text-acid' : 'text-white group-hover:text-acid'}`}>
                    {item.label}
                  </span>
                  <span className={`absolute bottom-0 left-0 h-[3px] bg-glitch transition-all duration-300 w-0 group-hover:w-full ${
                    isSectionActive ? 'w-full' : ''
                  }`}></span>
                </button>
              );
            })}
            
            <div className="relative">
              <button 
                onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
                className="ml-4 border border-zinc-700 rounded px-3 py-1 text-white font-mono text-xs hover:border-acid hover:text-acid transition-colors uppercase"
              >
                {language}
              </button>
              {isLangMenuOpen && (
                <div className="absolute right-0 mt-2 w-24 bg-zinc-900 border border-zinc-800 shadow-2xl rounded py-1 flex flex-col z-[120]">
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setIsLangMenuOpen(false); }}
                      className={`text-left px-4 py-2 font-mono text-xs hover:bg-zinc-800 ${language === lang ? 'text-acid' : 'text-zinc-500'}`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white font-mono text-2xl z-[210] p-4 -mr-4 focus:outline-none"
          >
            {isMobileMenuOpen ? 'X' : '///'}
          </button>
        </div>
      </header>

      {/* MOBILE OVERLAY */}
      <div 
        className={`fixed inset-0 bg-black z-[200] flex flex-col transition-all duration-500 ease-in-out md:hidden scanlines ${
          isMobileMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
        style={{ height: '100dvh' }}
      >
        <div className="flex-grow flex flex-col items-center justify-start p-8 pt-24 space-y-10 overflow-y-auto no-scrollbar">
          
          {/* Navegación Principal */}
          <div className="flex flex-col items-center space-y-6 w-full">
            {navItems.map((item, index) => {
              const isSectionActive = (currentView === ViewState.HOME || currentView === ViewState.DEMO_REEL) ? activeSection === item.id : item.view === currentView;
              return (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item)}
                  className={`flex items-center group transition-all duration-500 transform ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <span className="text-[10px] font-mono mr-4 text-zinc-600">0{index + 1}</span>
                  <span className={`font-display font-black text-5xl uppercase tracking-tighter ${
                    isSectionActive ? 'text-acid' : 'text-zinc-400'
                  }`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Selector de Idiomas Móvil */}
          <div className={`w-full max-w-[320px] pt-10 border-t border-zinc-900 transition-all duration-1000 delay-500 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}>
             <p className="font-mono text-[10px] text-zinc-600 text-center mb-6 uppercase tracking-[0.4em]">SYSTEM_LANGUAGE</p>
             <div className="grid grid-cols-3 gap-3">
               {languages.map((lang) => (
                 <button
                   key={lang}
                   onClick={() => { setLanguage(lang); setIsMobileMenuOpen(false); }}
                   className={`font-mono text-xs py-4 border-2 transition-all duration-200 ${
                     language === lang 
                     ? 'border-acid text-acid bg-acid/10 shadow-[4px_4px_0px_0px_#ccff00]' 
                     : 'border-zinc-800 text-zinc-500 bg-black'
                   }`}
                 >
                   {lang}
                 </button>
               ))}
             </div>
          </div>
          
          {/* Espaciador final para asegurar scroll */}
          <div className="py-4 opacity-0">.</div>
        </div>

        {/* Footer del Menú Móvil */}
        <div className="p-8 border-t border-zinc-900 bg-zinc-950 flex flex-col items-center">
           <span className="font-mono text-[9px] text-zinc-700 uppercase tracking-[0.5em]">¡RR3VERENT!A // MOBILE_v4.0</span>
        </div>
      </div>
    </>
  );
};
